import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';
import { BrowserEnvironmentService } from './browser-environment.service';

type AppTheme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly theme = signal<AppTheme>('light');
  private readonly browserEnvironment = inject(BrowserEnvironmentService);
  private readonly document = inject(DOCUMENT);

  constructor() {
    this.theme.set(this.getStoredTheme());

    effect(() => {
      const activeTheme = this.theme();
      this.setStoredTheme(activeTheme);
      this.applyThemeAttribute(activeTheme);
    });
  }

  toggleTheme(): void {
    this.theme.update((theme) => (theme === 'light' ? 'dark' : 'light'));
  }

  private getStoredTheme(): AppTheme {
    const storedTheme = this.browserEnvironment.getStorageItem('app-theme');
    return storedTheme === 'dark' ? 'dark' : 'light';
  }

  private setStoredTheme(theme: AppTheme): void {
    this.browserEnvironment.setStorageItem('app-theme', theme);
  }

  private applyThemeAttribute(theme: AppTheme): void {
    try {
      this.document.documentElement?.setAttribute('data-theme', theme);
      this.document.body?.setAttribute('data-theme', theme);
    } catch {
      // Document may be unavailable in non-browser environments.
    }
  }
}
